// Device-bound sessions — the browser half of the contract in
// lm/steve/auth.py.
//
// The backend binds a session to a second secret, the device key: it hands it
// to us in the `X-Device-Key` *response* header, we keep it in localStorage
// and send it back in the `X-Device-Key` *request* header. Past the
// server-side grace window a session request without the matching key is
// refused.
//
// What this actually buys, stated narrowly:
//
// * It defends against someone reading the session store — a Redis dump, a DB
//   or filesystem backup of it, a support tool that prints sessions. The store
//   holds only `_device_bound_at`, never the key; the key is derived from
//   SECRET_KEY, so a session-store copy alone no longer yields a usable
//   session.
// * It does NOT defend against a proxy or gateway that logs request headers:
//   the cookie and the key travel together on every call, so whoever reads one
//   reads both. Same for a host backup that also contains SECRET_KEY — every
//   key can be recomputed from a session key.
// * It does NOT defend against XSS on our origin: a script there reads
//   localStorage and can call the API with the cookie anyway. CSP is the
//   control for that, not this.
// * Binding is trust-on-first-use. An unbound session — one minted elsewhere
//   and never used against capi, e.g. by the Django admin — is stamped for
//   whoever presents it first. If a leaked cookie is used before its owner's
//   browser makes a call, the attacker's device is the one bound.
//
// Three rules are load-bearing:
//
// 1. Overwrite on every same-origin response that carries the header. Not "if
//    empty", not "if different" — always. A session key rotation (admin
//    `login_as`, a re-login after expiry) mints a brand new key while
//    localStorage still holds the previous one, and the unconditional
//    overwrite is what heals it.
// 2. Same-origin only, in BOTH directions. We never send the key off-origin
//    (`services.HealthCheck` talks to `api.<domain>`), and we never accept one
//    from off-origin either: a third-party response carrying this header would
//    otherwise overwrite the real key and lock the user out until the next
//    same-origin response healed it.
// 3. Never throw. localStorage is unreachable in a private window or with site
//    data blocked; there the app degrades to "no key" (and to a 401 after the
//    grace window), which still beats a white screen.
//
// Nothing here is logged or sent to analytics.

export const DEVICE_KEY_STORAGE = 'device_key';
export const DEVICE_KEY_HEADER = 'X-Device-Key';

export function readDeviceKey() {
    try {
        return localStorage.getItem(DEVICE_KEY_STORAGE) || null;
    } catch (e) {
        return null;
    }
}

export function writeDeviceKey(value) {
    try {
        localStorage.setItem(DEVICE_KEY_STORAGE, value);
    } catch (e) {
        /* private mode / blocked site data — carry on without a key */
    }
}

export function forgetDeviceKey() {
    try {
        localStorage.removeItem(DEVICE_KEY_STORAGE);
    } catch (e) {
        /* nothing to clear if storage is unreachable */
    }
}

// axios leaves `url` as given (this app never sets baseURL, but a future one
// might), so resolve exactly the way the browser will before comparing origins.
function requestOrigin(config) {
    const url = config && config.url ? String(config.url) : '';
    const base = config && config.baseURL ? String(config.baseURL) : '';
    const absolute = /^([a-z][a-z0-9+.-]*:)?\/\//i.test(url);
    const full = absolute || !base
        ? url
        : base.replace(/\/+$/, '') + '/' + url.replace(/^\/+/, '');
    return new URL(full, window.location.href).origin;
}

export function isSameOrigin(config) {
    try {
        return requestOrigin(config) === window.location.origin;
    } catch (e) {
        // An unparseable URL is not something we hand a secret to.
        return false;
    }
}

// axios 1.x wraps response headers in AxiosHeaders (case-insensitive `get`);
// plain objects with lowercased keys turn up in tests and in older versions.
function headerValue(headers) {
    if (!headers) return null;
    if (typeof headers.get === 'function') return headers.get(DEVICE_KEY_HEADER) || null;
    return headers[DEVICE_KEY_HEADER.toLowerCase()] || headers[DEVICE_KEY_HEADER] || null;
}

export function rememberFromResponse(response) {
    // Mirror of the request-side guard: only our own origin may set the key.
    // A third-party endpoint controls its own CORS headers, so it can expose
    // an `X-Device-Key` of its choosing — and without this check it would be
    // planting a session secret in our localStorage. `response.config` is what
    // axios echoes back on success and on `error.response` alike; when it is
    // missing we cannot tell who answered, so we do not write. Same-origin
    // responses keep the unconditional overwrite (rule 1 at the top).
    if (!response || !response.config || !isSameOrigin(response.config)) return;
    const value = headerValue(response.headers);
    if (value) writeDeviceKey(value);
}

export function installDeviceKey(axios) {
    axios.interceptors.request.use(config => {
        const key = isSameOrigin(config) ? readDeviceKey() : null;
        if (key) {
            // AxiosHeaders in axios 1.x; a plain object anywhere else.
            if (config.headers && typeof config.headers.set === 'function') {
                config.headers.set(DEVICE_KEY_HEADER, key);
            } else {
                config.headers = Object.assign({}, config.headers, { [DEVICE_KEY_HEADER]: key });
            }
        }
        return config;
    });
    axios.interceptors.response.use(
        response => {
            rememberFromResponse(response);
            return response;
        },
        error => {
            // A network failure has no response at all; `rememberFromResponse`
            // shrugs at undefined rather than turning it into a second error.
            rememberFromResponse(error && error.response);
            return Promise.reject(error);
        }
    );
}

export default installDeviceKey;
